import { useSearchParams } from '@solidjs/router';
import { gql } from 'graphql-request';
import { For, Show, batch, createSignal, mergeProps, onMount, createEffect, on, createMemo } from 'solid-js';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { ButtonColorName } from '../../style/color-classes';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Button } from '../../ui-components/button/button';
import { CategoryFilter } from '../../ui-components/category-filter/category-filter';
import { ProjectListItem } from './project-list-item';
import { PostListingSchema } from '../schema/post-listing';

export type PostContainer = {
    content: any;
};

export type Category = {
    name: string;
    slug: string;
    id?: number;
};

const defaultProps: BlogArchiveProps = {
    categories: [],
    projectCategoriesFromHook: [],
};

const GET_PROJECTS_QUERY = gql`
    query GetProjects($tags: [String], $limit: Int, $offset: Int) {
        posts: resources(type: "project", tags: $tags, limit: $limit, offset: $offset, order: DESC) {
            content
        }
    }
`;

export type BlogArchiveProps = {
    tag?: {
        slug: string;
        name?: string;
    };
    projectCategoriesFromHook: Array<any>;
    categories?: Category[];
    chosenCategories?: string[];
    hideFilter?: boolean;
    postType?: string;
};

export const ProjectArchive = (componentProps: BlogArchiveProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamsCategory = searchParams.category;
    const props = mergeProps(defaultProps, componentProps);
    const { createCachedResource, translate } = useAppContext();

    const [offset, setOffset] = createSignal(0);
    const [selectedFilter, setSelectedFilter] = createSignal<string | undefined>(searchParamsCategory ? searchParamsCategory : undefined);
    
    const [limit] = createSignal(20);
    const [posts, setPosts] = createSignal<any>([]);
    const [displayLoadMoreButton, setDisplayLoadMoreButton] = createSignal(false);


    const queryVars = createMemo(() => {
        return {
            tags: selectedFilter(),
            limit: limit() + 1,
            offset: offset(),
        };
    });

    
    const [postsContent] = createCachedResource(GET_PROJECTS_QUERY, queryVars, true);

    const handleLoadMoreClick = () => {
        setOffset(offset() + limit());
    };

    const handleFilterClick = (slug: string | undefined) => {
        setSearchParams({ category: slug });
    };

    // Track the URL so back/forward re-filters, not just clicks on the chips.
    createEffect(on(() => searchParams.category, (category) => {
        batch(() => {
            setSelectedFilter(category || undefined);
            setOffset(0);
            setPosts([]);
        });
    }, { defer: true }));

    createEffect(on(postsContent, (content) => {
        if (!content) return;

        setDisplayLoadMoreButton(content.posts.length > limit());
        setPosts(prev => {
            if (offset() === 0) {
                return content.posts.slice(0, limit());
            }
            return [...prev, ...content.posts.slice(0, limit())];
        });
    }));

    onMount(() => {
        if (componentProps.chosenCategories?.length === 1) {
            setSelectedFilter(componentProps.chosenCategories[0]);
        }
    });

    return (
        <ErrorCatcher componentName="ProjectArchive">
            <div>
                <Show when={!componentProps.hideFilter}>
                    <CategoryFilter
                        categories={props.projectCategoriesFromHook}
                        selectedSlug={selectedFilter()}
                        onSelect={handleFilterClick}
                    />
                </Show>
                <Show when={postsContent() && posts().length > 0} fallback={<div class="h-screen"></div>}>
                    <div class="post-list tablet:grid-cols-2 laptop:grid-cols-3 desktopXL:grid-cols-4 gap-y-xl gap-x-l tablet:gap-y-xxl tablet:gap-x-xxl mb-xl grid grid-cols-1"
                        classList={{ 'laptop:grid-cols-2 desktopXL:grid-cols-2 tablet:gap-x-l tablet:gap-y-l' : componentProps.postType === 'client'}}
                    >
                        <For each={posts()}>
                            {(post) => {
                                return <ProjectListItem post={post.content} />;
                            }}
                        </For>
                    </div>
                    <Show when={displayLoadMoreButton()}>
                        <div class="text-center">
                            <Button variant="solid" color={ButtonColorName.Primary} onClick={() => handleLoadMoreClick()} label={translate('load-more')} buttonSize="small" />
                        </div>
                    </Show>
                </Show>
            </div>

            <PostListingSchema
                content={postsContent()?.posts}
                postSingleType="ListItem"
                postListingType="itemListElement"
            />
        </ErrorCatcher>
    );
};
